import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaBlog, FaSave, FaUndo } from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';

const BlogPageCMS = () => {
  const { success: toastSuccess, error: toastError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    hero: { title: 'Blog', subtitle: '' },
    blogsPerPage: 7,
    isPublished: true,
  });

  const fetchPage = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/blog-page/admin`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        const d = res.data.data;
        setForm({
          hero: d.hero || { title: 'Blog', subtitle: '' },
          blogsPerPage: d.blogsPerPage ?? 7,
          isPublished: d.isPublished !== false,
        });
      }
    } catch (err) {
      toastError('Failed to load blog page settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPage(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/blog-page/admin`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Blog page settings saved');
        fetchPage();
      }
    } catch (err) {
      toastError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Restore default blog page settings?')) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/cms/blog-page/admin/reset`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        toastSuccess('Defaults restored');
        fetchPage();
      }
    } catch (err) {
      toastError('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title="Blog Page CMS"
        description="Edit the blog listing page hero and pagination settings"
        icon={FaBlog}
        actions={
          <div className="flex gap-2 flex-wrap">
            <button onClick={handleReset} disabled={saving || loading} className="px-4 py-2.5 rounded-xl border border-gray-200 dark:border-dark-600 text-sm font-medium flex items-center gap-2">
              <FaUndo /> Reload Defaults
            </button>
            <button onClick={handleSave} disabled={saving || loading} className="btn-premium flex items-center gap-2">
              <FaSave /> {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        }
      />

      {loading ? (
        <SkeletonLoader variant="page" />
      ) : (
        <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-card border border-gray-100 dark:border-dark-700 p-6 space-y-5">
          <div className="flex justify-between items-center border-b border-gray-100 dark:border-dark-700 pb-4">
            <h2 className="text-lg font-bold">Blog Page Header</h2>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isPublished} onChange={(e) => setForm((p) => ({ ...p, isPublished: e.target.checked }))} />
              Published
            </label>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase text-gray-500">Title</label>
            <input className="input-modern" value={form.hero.title} onChange={(e) => setForm((p) => ({ ...p, hero: { ...p.hero, title: e.target.value } }))} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase text-gray-500">Subtitle</label>
            <textarea className="input-modern min-h-[80px]" value={form.hero.subtitle} onChange={(e) => setForm((p) => ({ ...p, hero: { ...p.hero, subtitle: e.target.value } }))} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase text-gray-500">Blogs Per Page</label>
            <input className="input-modern w-32" type="number" min={1} max={50} value={form.blogsPerPage} onChange={(e) => setForm((p) => ({ ...p, blogsPerPage: Number(e.target.value) }))} />
          </div>
          <p className="text-sm text-gray-500">Individual blog posts are managed under Blogs in the admin sidebar.</p>
        </div>
      )}
    </PageLayout>
  );
};

export default BlogPageCMS;
